import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BroadcastIcon, CopyIcon, CheckCircleIcon, ArrowsClockwiseIcon, TerminalIcon, DownloadSimpleIcon } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Bridge() {
  const [status, setStatus] = useState({ connected: false, last_sync: null, live_counts: {} });
  const [copied, setCopied] = useState(null);
  const [token] = useState("qf_UoAyL6e5imfBkXggw-UnRMKihwbqmkOa");

  const refresh = () => api.get("/agent/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => { refresh(); const t = setInterval(refresh, 10000); return () => clearInterval(t); }, []);

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key); setTimeout(() => setCopied(null), 1500);
  };

  const setupBlock = `setx QF_API_URL "${BACKEND_URL}" /M\nsetx BRIDGE_TOKEN "${token}" /M`;

  return (
    <div data-testid="bridge-page" className="p-6 space-y-4 max-w-4xl">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Stage D · Real-Time Sync</div>
        <h1 className="text-2xl tracking-tight font-medium mt-1">Bridge</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
          Connects your Windows VPS to this dashboard. After every master pipeline run, your bot results, AI decisions, and hypothesis outcomes get pushed here so you can view your real fleet from anywhere.
        </p>
      </div>

      {/* Live status card */}
      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-3 h-3 rounded-full ${status.connected ? "bg-[#2ECA7F] animate-pulse" : "bg-[#64748B]"}`}
              data-testid="bridge-status-dot"
            />
            <div>
              <div className="text-sm font-medium">{status.connected ? "Bridge Connected" : "Bridge Offline"}</div>
              <div className="text-[11px] mono text-[#94A3B8] mt-0.5">
                {status.last_sync ? `last sync: ${new Date(status.last_sync).toLocaleString()}` : "no data received yet"}
              </div>
            </div>
          </div>
          <button onClick={refresh} data-testid="refresh-status" className="text-[11px] mono uppercase tracking-widest text-[#94A3B8] hover:text-[#D6FF38] flex items-center gap-1">
            <ArrowsClockwiseIcon size={12} /> refresh
          </button>
        </div>
        {Object.keys(status.live_counts || {}).length > 0 && (
          <div className="mt-4 grid grid-cols-4 gap-3">
            {["leaderboard", "decisions", "proposals", "outcomes"].map((k) => (
              <div key={k} className="border border-[#2A2D3A] rounded p-3">
                <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">{k}</div>
                <div className="mono text-lg mt-1">{status.live_counts[k] ?? "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Setup instructions */}
      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-5">
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-2">
          <TerminalIcon size={12} /> One-time setup on your VPS
        </div>

        <ol className="space-y-4">
          <li>
            <div className="text-sm">1. Download v7.0 (JSON parser fix — reads native cTrader BalanceDD/EquityDD + WinRate directly from report JSON):</div>
            <a
              href="/BotGenomix-v69-bridge-update.zip"
              download
              data-testid="bridge-download"
              className="mt-1 inline-flex items-center gap-2 text-[11px] mono text-[#D6FF38] hover:underline"
            >
              <DownloadSimpleIcon size={12} /> BotGenomix-v69-bridge-update.zip
            </a>
            <div className="text-xs text-[#94A3B8] mt-1">
              Extract to <span className="mono">C:\BotJudge\Scripts\</span> (overwrite), then{" "}
              <span className="mono">Get-ChildItem ...\Scripts\*.ps1,*.py | Unblock-File</span>.
              After copy, run <span className="mono">python build_monthly_results.py --rebuild</span> once
              to reparse every HTML report with the new JSON extractor.
            </div>
          </li>

          <li>
            <div className="text-sm">2. Set the two environment variables on your VPS (admin PowerShell):</div>
            <div className="relative mt-2">
              <pre data-testid="setup-commands" className="bg-[#0B0C10] border border-[#2A2D3A] rounded p-3 text-[11px] mono text-[#F1F5F9] overflow-x-auto whitespace-pre">
{setupBlock}
              </pre>
              <button
                onClick={() => copy("setup", setupBlock)}
                data-testid="copy-setup"
                className="absolute top-2 right-2 text-[10px] mono uppercase tracking-widest px-2 py-1 rounded bg-[#15171E] border border-[#2A2D3A] hover:border-[#D6FF38]/40 text-[#94A3B8] hover:text-[#D6FF38]"
              >
                {copied === "setup" ? <CheckCircleIcon size={12} weight="fill" /> : <CopyIcon size={12} />}
              </button>
            </div>
            <div className="text-xs text-[#94A3B8] mt-1">Close and re-open PowerShell after running these.</div>
          </li>

          <li>
            <div className="text-sm">3. Run the master pipeline as usual:</div>
            <div className="relative mt-2">
              <pre className="bg-[#0B0C10] border border-[#2A2D3A] rounded p-3 text-[11px] mono text-[#F1F5F9]">
{`cd C:\\BotJudge\\Scripts
.\\Run_BotJudge_Master.ps1`}
              </pre>
              <button
                onClick={() => copy("run", "cd C:\\BotJudge\\Scripts\n.\\Run_BotJudge_Master.ps1")}
                className="absolute top-2 right-2 text-[10px] mono uppercase tracking-widest px-2 py-1 rounded bg-[#15171E] border border-[#2A2D3A] hover:border-[#D6FF38]/40 text-[#94A3B8] hover:text-[#D6FF38]"
              >
                {copied === "run" ? <CheckCircleIcon size={12} weight="fill" /> : <CopyIcon size={12} />}
              </button>
            </div>
            <div className="text-xs text-[#94A3B8] mt-1">
              The pipeline now ends with a &ldquo;BRIDGE TO BOTGENOMIX DASHBOARD&rdquo; step. After it completes, this page will show &ldquo;Connected&rdquo; and your fleet will appear in the Overview / Leaderboard.
            </div>
          </li>

          <li>
            <div className="text-sm">4. Push manually any time (skip the full pipeline):</div>
            <div className="relative mt-2">
              <pre className="bg-[#0B0C10] border border-[#2A2D3A] rounded p-3 text-[11px] mono text-[#F1F5F9]">.\\run_bridge.ps1</pre>
              <button
                onClick={() => copy("manual", ".\\run_bridge.ps1")}
                className="absolute top-2 right-2 text-[10px] mono uppercase tracking-widest px-2 py-1 rounded bg-[#15171E] border border-[#2A2D3A] hover:border-[#D6FF38]/40 text-[#94A3B8] hover:text-[#D6FF38]"
              >
                {copied === "manual" ? <CheckCircleIcon size={12} weight="fill" /> : <CopyIcon size={12} />}
              </button>
            </div>
          </li>
        </ol>

        {/* Command cheatsheet */}
        <div className="mt-6 pt-5 border-t border-[#2A2D3A]" data-testid="command-cheatsheet">
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-2">
            <TerminalIcon size={12} /> PowerShell Cheatsheet · Autopilot Operator Commands
          </div>
          <div className="space-y-2.5">
            <CmdRow label="Start endless autopilot loop (with live progress reporting)" cmd={`cd C:\\BotJudge\\Scripts\nStart-Job -Name "progress-reporter" -FilePath .\\progress_reporter.ps1 | Out-Null\n.\\Run_Forever.ps1`} copy={copy} copied={copied} k="cmd-forever" note="First line launches the progress heartbeat as a background job in the same terminal - the Overview tile updates every 60s. Second line runs the forever loop as normal." />
            <CmdRow label="Stop the loop and progress reporter" cmd={`Ctrl + C\nStop-Job -Name "progress-reporter"; Remove-Job -Name "progress-reporter"`} copy={copy} copied={copied} k="cmd-stop" note="Ctrl+C stops the loop, then run the Stop-Job line to clean up the background reporter." />
            <CmdRow label="One-shot cycle (master pipeline once, no loop)" cmd={`.\\Run_BotJudge_Master.ps1`} copy={copy} copied={copied} k="cmd-once" />
            <CmdRow label="Resume last cycle - retry only FAILED steps" cmd={`.\\Run_BotJudge_Master.ps1 -Resume`} copy={copy} copied={copied} k="cmd-resume" />
            <CmdRow label="Push data to dashboard manually" cmd={`.\\run_bridge.ps1`} copy={copy} copied={copied} k="cmd-push" />
            <CmdRow label="Dry-run bridge (see what WOULD be pushed)" cmd={`python .\\bridge_agent.py --dry-run`} copy={copy} copied={copied} k="cmd-dry" />
            <CmdRow label="Deploy Hall of Fame Top-3 to LIVE_ cBots" cmd={`.\\Deploy_Live.ps1`} copy={copy} copied={copied} k="cmd-deploy" note="Stages LIVE_ cBot folders. Run after promoting bots from the dashboard." />
            <CmdRow label="Push live paper-trading P&L to dashboard" cmd={`python .\\live_bridge.py`} copy={copy} copied={copied} k="cmd-live" />
            <CmdRow label="Seed diversified fleet (17 cross-asset bots)" cmd={`.\\Seed_Diverse_Fleet.ps1`} copy={copy} copied={copied} k="cmd-seed" note="Idempotent - backfills missing BotConfig entries too." />
            <CmdRow label="Unblock all downloaded PS/Python files" cmd={`Get-ChildItem C:\\BotJudge\\Scripts\\*.ps1,*.py | Unblock-File`} copy={copy} copied={copied} k="cmd-unblock" />
            <CmdRow label="Check env vars are set" cmd={`echo $env:QF_API_URL\necho $env:BRIDGE_TOKEN`} copy={copy} copied={copied} k="cmd-env" />
            <CmdRow label="Tail today's bridge log (JSON)" cmd={`Get-ChildItem C:\\BotJudge\\Logs\\bridge_*.json | Sort-Object LastWriteTime -Descending | Select-Object -First 1 | Get-Content`} copy={copy} copied={copied} k="cmd-log" />
            <CmdRow label="Kill a stuck cTrader process" cmd={`Get-Process cTrader* | Stop-Process -Force`} copy={copy} copied={copied} k="cmd-kill" note="Only if cTrader itself hangs mid-backtest." />
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-[#2A2D3A] text-[11px] text-[#64748B] leading-relaxed">
          <BroadcastIcon size={12} className="inline -mt-0.5 mr-1 text-[#D6FF38]" />
          Privacy: bot source code never leaves your VPS. Only backtest metrics + AI judge text are pushed.
        </div>
      </div>
    </div>
  );
}

function CmdRow({ label, cmd, copy, copied, k, note }) {
  return (
    <div className="border border-[#2A2D3A] bg-[#0B0C10] rounded-md p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] text-[#94A3B8] flex-1">{label}</div>
        <button
          data-testid={k}
          onClick={() => copy(k, cmd)}
          className="text-[10px] mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#15171E] border border-[#2A2D3A] hover:border-[#D6FF38]/40 text-[#94A3B8] hover:text-[#D6FF38] shrink-0"
        >
          {copied === k ? <CheckCircleIcon size={11} weight="fill" /> : <CopyIcon size={11} />}
        </button>
      </div>
      <pre className="mt-1.5 text-[11px] mono text-[#F1F5F9] whitespace-pre-wrap break-all">{cmd}</pre>
      {note && <div className="mt-1.5 text-[10px] text-[#64748B] italic">{note}</div>}
    </div>
  );
}
